import { useEffect, useState } from "react";
import { FileSpreadsheet, FileText, BookOpen, BarChart3, Store, Calendar, Disc } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { exportUrl, getReleases } from "@/lib/afmApi";
import { SectionTitle } from "@/components/common";

const STORES = [
  { key: "", label: "Todas las tiendas" },
  { key: "spotify", label: "Spotify" },
  { key: "apple", label: "Apple Music" },
  { key: "youtube", label: "YouTube Music" },
  { key: "amazon", label: "Amazon Music" },
  { key: "deezer", label: "Deezer" },
  { key: "tidal", label: "TIDAL" },
];

const KINDS = [
  { key: "accounting", label: "Libro Contable", desc: "N° transacción, fecha, canción, ISRC, UPC, plataforma, país, streams, bruto, % y neto.", icon: BookOpen },
  { key: "insights", label: "Reporte de Insights", desc: "Reproducciones, oyentes, plataformas, países, ciudades y demografía.", icon: BarChart3 },
];

export default function Exports() {
  const { user } = useAuth();
  const [kind, setKind] = useState("accounting");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [store, setStore] = useState("");
  const [releaseId, setReleaseId] = useState("");
  const [releases, setReleases] = useState([]);

  useEffect(() => {
    getReleases(user.id).then(setReleases).catch(() => setReleases([]));
  }, [user.id]);

  const download = (format) => {
    const url = exportUrl(user.id, {
      kind,
      format,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      store: store || undefined,
      release_id: releaseId || undefined,
    });
    window.open(url, "_blank");
  };

  const inputCls =
    "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-black";

  return (
    <div className="space-y-8" data-testid="exports-page">
      <p className="max-w-2xl text-sm text-slate-500">
        Genera y descarga tus informes en Excel (.xlsx) o CSV. Aplica filtros de rango de
        fecha, lanzamiento y tienda para obtener exactamente lo que necesitas.
      </p>

      {/* Report type */}
      <div>
        <SectionTitle>Tipo de informe</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2">
          {KINDS.map((k) => (
            <button
              key={k.key}
              data-testid={`export-kind-${k.key}`}
              onClick={() => setKind(k.key)}
              className={`flex items-start gap-4 rounded-xl border p-5 text-left transition-all ${
                kind === k.key ? "border-black bg-black text-white" : "border-slate-200 bg-white hover:border-slate-400"
              }`}
            >
              <div className={`rounded-lg p-2.5 ${kind === k.key ? "bg-white/10" : "bg-slate-100"}`}>
                <k.icon className="h-5 w-5" />
              </div>
              <div>
                <div className="font-serif text-lg font-bold">{k.label}</div>
                <div className={`mt-1 text-xs ${kind === k.key ? "text-slate-300" : "text-slate-500"}`}>{k.desc}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="export-filters">
        <SectionTitle>Filtros</SectionTitle>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600">
              <Calendar className="h-3.5 w-3.5" /> Desde
            </label>
            <input data-testid="export-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600">
              <Calendar className="h-3.5 w-3.5" /> Hasta
            </label>
            <input data-testid="export-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600">
              <Store className="h-3.5 w-3.5" /> Tienda
            </label>
            <select data-testid="export-store" value={store} onChange={(e) => setStore(e.target.value)} className={inputCls}>
              {STORES.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600">
              <Disc className="h-3.5 w-3.5" /> Lanzamiento
            </label>
            <select data-testid="export-release" value={releaseId} onChange={(e) => setReleaseId(e.target.value)} className={inputCls}>
              <option value="">Todos los lanzamientos</option>
              {releases.map((r) => (
                <option key={r.id} value={r.id}>{r.title}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            data-testid="export-excel-button"
            onClick={() => download("xlsx")}
            className="flex items-center gap-2 rounded-lg bg-black px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
          >
            <FileSpreadsheet className="h-4 w-4" /> Descargar Excel (.xlsx)
          </button>
          <button
            data-testid="export-csv-button"
            onClick={() => download("csv")}
            className="flex items-center gap-2 rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold text-black transition-colors hover:border-black"
          >
            <FileText className="h-4 w-4" /> Descargar CSV
          </button>
        </div>
      </div>
    </div>
  );
}
